import { useCallback, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  steppedHPConfigurations,
  hpFormStepConfigurations,
  hangingProtocolStepIndex,
  hangingProtocolSubStepIndex,
  getStepsFromVersioned,
} from '../state';
import type { StepsUpdater } from '../state';
import { useClinicalStepWriters } from '../clinicalStepStore';
// Import via the Toaster barrel (not the concrete module) so unit tests that mock
// 'common/ui/Toaster' also intercept this hook's toaster dependency (VX-4444).
import { useToasterDispatch } from 'common/ui/Toaster';
import { createStepToastContentComponent, getDefaultHPToastOptions } from '../Steps/StepToast';
import { HP_TOAST_UNDO_DURATION } from '../Steps/hpStepToastMessages';
import { FORM_STEP_CREATED_TOAST, STEP_DELETED_TOAST } from '../Steps/stepsToastFactory';
import type { StepConfiguration } from '../steppedHPTypes';
import {
  BASE_STEP_NAME,
  createDefaultStep,
  generateDefaultStepName,
  isStepNameDuplicate,
} from '../hpStepController';
import {
  getStepSwitchInterceptor,
  getFormFlushInterceptor,
  stepLayoutsToLayouts,
} from './useStepFormSync';
import { useViewerLayoutWriters } from '../../Viewer/StudyLoader/viewerLayoutWriters';
import { DropPosition } from '../Steps/dragDropUtils';
import { parseTempStepId } from '../tempStepId';
export const MAX_HP_STEPS = 30;

// Module-level set of active undo toast keys. Kept outside the hook so that
// dismissPendingUndoToasts works correctly even when StepBar temporarily
// unmounts and remounts (e.g., when the HP edit sidebar opens, ResizableStepBar
// returns null which unmounts StepBar; the keys must survive the remount).
const activeUndoToastKeys = new Set<string>();

/**
 * Resolves a step identifier to an array index. Handles both real smids and synthetic `__index_N`
 * fallback IDs generated by StepsConfigSection for steps without a backend smid.
 */
function resolveStepIndex(stepId: string, steps: StepConfiguration[]): number {
  const tempIndex = parseTempStepId(stepId);
  if (tempIndex !== null) {
    return tempIndex;
  }
  return steps.findIndex((step) => step.smid === stepId);
}

/**
 * Outcome of {@link UseHPStepOperationsReturn.insertStep}. `max_steps` means the protocol already has `MAX_HP_STEPS`
 * steps and nothing was inserted.
 */
export type InsertStepResult = { ok: true } | { ok: false; reason: 'max_steps' };

/**
 * Builds the step to insert. Receives the step list after pending form edits are flushed, and the index of the active
 * step clamped into that list, so a factory can name or clone against what the reader actually has in front of them.
 */
export type StepFactory = (
  currentSteps: StepConfiguration[],
  activeStepIndex: number
) => StepConfiguration;

export type DropTarget = {
  index: number;
  position: DropPosition;
};

export type StepEditOptions = {
  /**
   * False commits the edit with no undo toast. The Step Library passes it: its Cancel already undoes the session as a
   * whole, and an undo toast for one edit inside it would restore a list from part-way through.
   */
  undoable?: boolean;
};

export type DeleteStepOptions = StepEditOptions & {
  /** Called once the delete is final: when its undo toast closes without UNDO, or at once when not undoable. */
  onDismiss?: () => void;
  onUndo?: () => void;
  allowLastStep?: boolean;
};

/** A step list and its active step, taken by `captureSteps` for `restoreSteps` to put back. */
export type StepsSnapshot = {
  steps: StepConfiguration[];
  stepIndex: number;
};

export type UseHPStepOperationsReturn = {
  steps: StepConfiguration[] | null;
  currentStepIndex: number;

  // Step lifecycle
  /**
   * Seeds the step list the form works on and says which step it opens on.
   *
   * `stepIndex` defaults to 0, but the HP form passes the step the reader was on: homing to 0
   * unconditionally moved the reader off their step *and* made the form load step 0's viewports and grid
   * while the chips still showed step N as active, so Add Step cloned the wrong step (VX-4810).
   */
  initializeSteps: (steps: StepConfiguration[] | null, options?: { stepIndex?: number }) => void;
  resetSteps: () => void;

  // Step CRUD
  /**
   * Inserts the step `buildStep` returns at `atIndex` (clamped; default: the end) and navigates to it, with an undo
   * toast unless `undoable` is false.
   */
  insertStep: (
    buildStep: StepFactory,
    atIndex?: number,
    options?: StepEditOptions
  ) => InsertStepResult;
  addStep: () => void;
  /**
   * Removes a step, with an undo toast unless `undoable` is false. Refuses to remove the last one unless
   * `allowLastStep` is set, which only the Step Library does: it lets a template replace Step 1 and keeps the protocol
   * from closing with no steps.
   */
  deleteStep: (stepId: string, options?: DeleteStepOptions) => void;

  renameStep: (stepId: string, newName: string) => void;
  /** Moves the index only. Use {@link moveToStepIndex} to make a different step active. */
  setCurrentStepIndex: (index: number) => void;
  /** Makes the step at `index` active, starting at its first sub-step. */
  moveToStepIndex: (index: number) => void;

  // Step navigation
  stepTotal: number;
  canGoBack: boolean;
  canGoForward: boolean;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  goToFirstStep: () => void;
  goToLastStep: () => void;

  // Drag-and-drop
  draggedIndex: number | null;
  dropTarget: DropTarget | null;
  handleStepBarDragStart: (index: number) => void;
  handleStepBarDragOver: (index: number, position: DropPosition) => void;
  handleStepBarDragEnd: () => void;
  /**
   * Applies a step-bar drag-drop reorder. Returns the resulting step smid order, or `null` when the
   * drop was a no-op (dropped back onto its own position, or nothing was being dragged), so callers
   * can move index-addressed side state (baseline entries, revert snapshots) along with the steps
   * without re-deriving the insert math.
   */
  handleStepBarDrop: (targetIndex: number) => ReadonlyArray<Nullable<string>> | null;
  /** Moves the step at `fromIndex` to `toIndex` (clamped), keeping the active step selected. The non-drag reorder. */
  moveStep: (fromIndex: number, toIndex: number) => void;

  // Session snapshots
  /**
   * Flushes pending form edits and returns the step list and active step. The Step Library takes one on open so that
   * Cancel can put the protocol back the way it was.
   */
  captureSteps: () => StepsSnapshot;
  /** Puts back a `captureSteps` snapshot, dismissing pending undo toasts, whose own snapshots it supersedes. */
  restoreSteps: (snapshot: StepsSnapshot) => void;

  // Rename UI state
  renamingStepId: string | null;
  handleStartRename: (stepId: string) => void;
  handleRenameConfirm: (stepId: string, newName: string) => void;
  handleRenameCancel: () => void;

  // Toast management
  dismissPendingUndoToasts: () => void;
};

/**
 * Injected atom accessors that decouple the core hook from which atoms it reads/writes.
 * Viewer mode and form mode provide different implementations.
 */
type StepAtomAccessors = {
  steps: StepConfiguration[] | null;
  setSteps: (updater: StepsUpdater) => void;
  /**
   * Syncs renames to the opposite atom (viewer ↔ form). Only `renameStep` calls
   * this; add/delete/reorder skip it intentionally. Null = no sync needed.
   */
  syncRename: ((updater: StepsUpdater) => void) | null;
  /** Flush interceptor for flushing form edits before add/delete. Null = none. */
  flushInterceptor: (() => StepConfiguration[] | null) | null;
  /**
   * Called with the newly added step once it has been appended and selected.
   *
   * The form needs this because `addStep` moves the step index itself rather than going through
   * `useStepFormSync.switchToStep`, which is the only other thing that publishes an arriving step's
   * grid. Null = nothing to publish; viewer mode reaches the layout atoms through its own clinical
   * path. Declared non-optional so a new caller has to state which it is (VX-4810).
   */
  onStepAdded: ((step: StepConfiguration) => void) | null;
  /**
   * Called with the step that is now active when an operation puts a different step in the active slot without going
   * through `switchToStep`: an UNDO of an add, and a `restoreSteps`. Needed for the same reason as `onStepAdded`. The
   * form keeps showing the departing step's viewports until something loads the new one, and its next flush writes
   * them over whichever step now holds that index. Null = nothing to publish.
   */
  onActiveStepReplaced: ((activeStep: StepConfiguration) => void) | null;
};

/** Default step when a HP has been created */
export const DEFAULT_CREATE_HP_STEP: StepConfiguration = createDefaultStep({
  name: BASE_STEP_NAME,
  defaultLayout: '2,2',
});

/**
 * Core hook that implements step CRUD, navigation, drag-and-drop, rename, and undo.
 * Decoupled from specific atoms via injected accessors.
 */
const useStepOperationsCore = ({
  steps: stepConfigurations,
  setSteps: setStepConfigurations,
  syncRename,
  flushInterceptor,
  onStepAdded,
  onActiveStepReplaced,
  ownsStepIndex,
}: StepAtomAccessors & { ownsStepIndex: boolean }): UseHPStepOperationsReturn => {
  const currentStepIndex = useRecoilValue(hangingProtocolStepIndex);
  const currentSubStepIndex = useRecoilValue(hangingProtocolSubStepIndex);
  const stepTotal = stepConfigurations?.length ?? 0;
  // Writes go through the store; the atoms are read directly. Keeping the local setter names means
  // the nine call sites and their dependency arrays below are unchanged by the move (VX-4810 3b).
  const {
    selectStep,
    selectStepWithIdentity,
    selectSubStep: setSubStepIndex,
  } = useClinicalStepWriters();

  // Viewer navigation moves within the very list the index selector resolves identity against, so it
  // can let the selector stamp the smid. The form navigates `hpFormStepConfigurations`, which can
  // diverge from the viewer's list, so slot N there is not slot N here -- letting the selector resolve
  // would record whichever *viewer* step happens to sit at that number, and the shared read would then
  // jump the reader onto it once the form's steps are saved into the viewer atom. The form writes
  // position only, which is what it has always meant. Giving it its own index state is the cleaner
  // shape and is deliberately still not this change (see `hangingProtocolStepIndex`).
  const setCurrentStepIndex = useCallback(
    (index: number) =>
      ownsStepIndex ? selectStep(index) : selectStepWithIdentity({ index, stepSmid: null }),
    [ownsStepIndex, selectStep, selectStepWithIdentity]
  );

  /**
   * Makes a different step the active one. A step change always starts at the first sub-step, since the reader's
   * sub-step belongs to the step they left, so every path that changes *which* step is active goes through here. A
   * write that only follows the same step to a new position -- a reorder, a delete above it -- calls
   * `setCurrentStepIndex` and keeps the sub-step, and an undo that puts the reader back restores theirs.
   */
  const moveToStepIndex = useCallback(
    (index: number) => {
      setCurrentStepIndex(index);
      setSubStepIndex(0);
    },
    [setCurrentStepIndex, setSubStepIndex]
  );

  const { enqueueToast, closeToast } = useToasterDispatch();

  // Drag-and-drop state
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);

  // Rename state
  const [renamingStepId, setRenamingStepId] = useState<string | null>(null);

  // Undo snapshots for step deletion (one per pending delete toast)
  const undoDeleteSnapshotsRef = useRef<
    Map<string, { previousStepConfigurations: StepConfiguration[]; onUndo?: () => void }>
  >(new Map());

  // Track toast keys where undo was clicked, so the subsequent toast
  // onClose knows to skip the onDismiss callback (which flushes the
  // deferred backend deletion).
  const undoedDeleteToastKeysRef = useRef<Set<string>>(new Set());

  // Undo snapshot for step addition (stores pre-add state for undo)
  const undoAddSnapshotRef = useRef<{
    previousStepConfigurations: StepConfiguration[] | null;
    previousStepIndex: number;
    previousSubStepIndex: number;
    toastKey: string;
  } | null>(null);

  const initializeSteps = useCallback(
    (steps: StepConfiguration[] | null, { stepIndex = 0 }: { stepIndex?: number } = {}) => {
      setStepConfigurations(steps);
      // Clamped rather than trusted. The caller's index is the reader's clinical position, measured
      // against the viewer's step list, and the list being seeded here can be shorter -- a step deleted
      // elsewhere, or a protocol whose steps the form fetched fresh. An index past the end resolves to
      // no step at all, which is the blank-viewport shape of VX-4868.
      const total = steps?.length ?? 0;
      moveToStepIndex(total === 0 ? 0 : Math.min(Math.max(stepIndex, 0), total - 1));
    },
    [setStepConfigurations, moveToStepIndex]
  );

  const resetSteps = useCallback(() => {
    setStepConfigurations(null);
    // `hangingProtocolStepIndex` and `hangingProtocolSubStepIndex` are shared with the clinical
    // step bar, so only the viewer's own operations may reset them. The form calls this from its
    // unmount cleanup to drop stale form data, and resetting the shared index there sent the step
    // bar back to step 0 every time the HP form closed -- including right after it had added a
    // step and moved to it. Measured as `useHPStepOperations addStep -> 1` followed by
    // `resetSteps -> 0` a few seconds later, once per form close.
    if (!ownsStepIndex) return;
    moveToStepIndex(0);
  }, [ownsStepIndex, setStepConfigurations, moveToStepIndex]);

  const handleUndoDelete = useCallback(
    (toastKey: string) => {
      const pending = undoDeleteSnapshotsRef.current.get(toastKey);
      if (!pending) return;

      setStepConfigurations(pending.previousStepConfigurations);
      // Let the caller (e.g. clinical mode) cancel its own pending backend
      // deletion and re-sync its dirty-state baseline for the restored step.
      pending.onUndo?.();

      // Mark this toast as undoed BEFORE closing so the onClose callback
      // skips onDismiss (which would otherwise flush the deferred deletion).
      undoedDeleteToastKeysRef.current.add(toastKey);
      undoDeleteSnapshotsRef.current.delete(toastKey);
      closeToast(toastKey);
    },
    [setStepConfigurations, closeToast]
  );

  const handleUndoAdd = useCallback(() => {
    const pending = undoAddSnapshotRef.current;
    if (!pending) return;

    setStepConfigurations(pending.previousStepConfigurations);
    setCurrentStepIndex(pending.previousStepIndex);
    setSubStepIndex(pending.previousSubStepIndex);
    const restoredStep = pending.previousStepConfigurations?.[pending.previousStepIndex];
    if (restoredStep) onActiveStepReplaced?.(restoredStep);

    closeToast(pending.toastKey);
    undoAddSnapshotRef.current = null;
  }, [
    setStepConfigurations,
    setCurrentStepIndex,
    setSubStepIndex,
    onActiveStepReplaced,
    closeToast,
  ]);

  const insertStep = useCallback(
    (buildStep: StepFactory, atIndex?: number, options?: StepEditOptions): InsertStepResult => {
      // When the form is open, flush current form edits into the step configurations
      // so the new step is built from the latest viewport data (not stale closure values).
      const flushed = flushInterceptor ? flushInterceptor() : null;
      const currentSteps = flushed ?? stepConfigurations ?? [];
      if (currentSteps.length >= MAX_HP_STEPS) return { ok: false, reason: 'max_steps' };

      // The step being cloned, resolved against the list actually in hand rather than trusting the index.
      // `currentStepIndex` is `hangingProtocolStepIndex`, which resolves the reader's `stepSmid` against
      // the *viewer's* steps and otherwise returns the stored index unclamped; `currentSteps` is the
      // form's list, a deliberately separate atom that is allowed to diverge (see the `stepSmid` doc in
      // `state.ts`). An index that is legal there can be past the end here, and the miss was silent:
      // `createDefaultStep` fell to its source-less branch and returned `layouts: {'0': '2,2'}` with no
      // viewports, so window 0 went to 2x2 explicitly and window 1, absent from the record, fell through
      // to `DEFAULT_LAYOUTS` -- the reported "Add Step came out 2x2 on both windows" on a protocol whose
      // steps were 1x1/1x2 and 1x2/1x1, matching neither (VX-4810). An empty list still yields the
      // source-less step, which is correct: that is Create's first one.
      const sourceStepIndex =
        currentSteps.length > 0
          ? Math.min(Math.max(currentStepIndex, 0), currentSteps.length - 1)
          : 0;

      // If there's an active undo toast from a previous add, dismiss it. Also before an edit with no undo of its
      // own: the older toast's snapshot predates this insert, so its UNDO would quietly drop it.
      if (undoAddSnapshotRef.current) {
        closeToast(undoAddSnapshotRef.current.toastKey);
        undoAddSnapshotRef.current = null;
      }

      const isUndoable = options?.undoable ?? true;
      const toastKey = `step-added-${Date.now()}`;

      if (isUndoable)
        undoAddSnapshotRef.current = {
          // Null only when there was no list at all. A list the Step Library emptied undoes back to empty, not to the
          // uninitialised `null` the form reads as "no steps loaded yet".
          previousStepConfigurations:
            currentSteps.length > 0 || stepConfigurations != null ? [...currentSteps] : null,
          // The clamped index, so undoing an add cannot re-strand the reader past the end of the restored
          // list -- which would leave the next Add Step in exactly the state above.
          previousStepIndex: sourceStepIndex,
          // The reader's sub-step belongs to the step they were on. When the clamp above moved the undo index onto a
          // different step, that step's first sub-step is the only position known to exist.
          previousSubStepIndex: sourceStepIndex === currentStepIndex ? currentSubStepIndex : 0,
          toastKey,
        };

      const newStep = buildStep(currentSteps, sourceStepIndex);
      // An index outside the list is clamped to its nearest end rather than rejected.
      const insertIndex =
        atIndex == null ? currentSteps.length : Math.min(Math.max(atIndex, 0), currentSteps.length);

      setStepConfigurations((prev) => {
        const base = prev ?? [];
        const at = Math.min(insertIndex, base.length);
        return [...base.slice(0, at), newStep, ...base.slice(at)];
      });
      moveToStepIndex(insertIndex);
      // Publish the clone's grid for the surface that just navigated to it. Moving the index is not
      // enough: nothing else reacts to it with the form's steps in hand, so the grid was left to be
      // derived from the *viewer's* step list, which does not contain this step (VX-4810).
      onStepAdded?.(newStep);

      if (!isUndoable) return { ok: true };

      activeUndoToastKeys.add(toastKey);
      enqueueToast(FORM_STEP_CREATED_TOAST.message, {
        ...getDefaultHPToastOptions({
          toastKey,
          autoHideDuration: HP_TOAST_UNDO_DURATION,
          onClose: () => {
            activeUndoToastKeys.delete(toastKey);
            if (undoAddSnapshotRef.current?.toastKey === toastKey) {
              undoAddSnapshotRef.current = null;
            }
            closeToast(toastKey);
          },
        }),
        ContentComponent: createStepToastContentComponent({
          actionLabel: FORM_STEP_CREATED_TOAST.actionLabel,
          countdownDuration: HP_TOAST_UNDO_DURATION,
          onAction: handleUndoAdd,
        }),
      });

      return { ok: true };
    },
    [
      flushInterceptor,
      stepConfigurations,
      currentStepIndex,
      currentSubStepIndex,
      setStepConfigurations,
      moveToStepIndex,
      onStepAdded,
      closeToast,
      enqueueToast,
      handleUndoAdd,
    ]
  );

  const addStep = useCallback(() => {
    insertStep((currentSteps, activeStepIndex) =>
      createDefaultStep({
        name: generateDefaultStepName(currentSteps),
        sourceStep: currentSteps[activeStepIndex],
      })
    );
  }, [insertStep]);

  const deleteStep = useCallback(
    (stepId: string, options?: DeleteStepOptions) => {
      // When the form is open, flush current form edits so the undo
      // snapshot captures the latest viewport data.
      const currentSteps =
        (flushInterceptor ? flushInterceptor() : null) ?? stepConfigurations ?? [];
      const minimumSteps = options?.allowLastStep ? 0 : 1;
      if (currentSteps.length <= minimumSteps) return;

      // Validate the index before allocating the toast key / undo snapshot so a bad stepId
      // doesn't leave a stale entry in undoDeleteSnapshotsRef with no toast to clean it up.
      const deletedIndex = resolveStepIndex(stepId, currentSteps);
      if (deletedIndex < 0) return;

      const isUndoable = options?.undoable ?? true;
      const toastKey = `step-deleted-${Date.now()}`;

      if (isUndoable)
        undoDeleteSnapshotsRef.current.set(toastKey, {
          previousStepConfigurations: [...currentSteps],
          onUndo: options?.onUndo,
        });

      const newSteps = currentSteps.filter((_, i) => i !== deletedIndex);
      setStepConfigurations(newSteps);

      const lastIndex = Math.max(newSteps.length - 1, 0);
      if (deletedIndex === currentStepIndex) {
        // The active step is gone, so whichever step takes its slot (or the new last one) is a different step.
        moveToStepIndex(Math.min(deletedIndex, lastIndex));
      } else if (deletedIndex < currentStepIndex) {
        // The active step only moved up one place, so the reader keeps their sub-step. Clamped, as before, for an index
        // that was already past the end of the list.
        setCurrentStepIndex(Math.min(currentStepIndex - 1, lastIndex));
      }
      // No undo window, so the delete is final now.
      if (!isUndoable) {
        options?.onDismiss?.();
        return;
      }

      activeUndoToastKeys.add(toastKey);
      enqueueToast(STEP_DELETED_TOAST.message, {
        ...getDefaultHPToastOptions({
          toastKey,
          autoHideDuration: HP_TOAST_UNDO_DURATION,
          onClose: () => {
            activeUndoToastKeys.delete(toastKey);
            undoDeleteSnapshotsRef.current.delete(toastKey);
            // Skip onDismiss (which flushes the deferred deletion) when
            // undo was clicked — the undo handler marks the toast key.
            const wasUndoed = undoedDeleteToastKeysRef.current.has(toastKey);
            undoedDeleteToastKeysRef.current.delete(toastKey);
            if (!wasUndoed) {
              options?.onDismiss?.();
            }
            closeToast(toastKey);
          },
        }),
        ContentComponent: createStepToastContentComponent({
          actionLabel: STEP_DELETED_TOAST.actionLabel,
          countdownDuration: HP_TOAST_UNDO_DURATION,
          onAction: () => handleUndoDelete(toastKey),
          icon: STEP_DELETED_TOAST.icon,
        }),
      });
    },
    [
      flushInterceptor,
      stepConfigurations,
      currentStepIndex,
      setStepConfigurations,
      setCurrentStepIndex,
      moveToStepIndex,
      closeToast,
      enqueueToast,
      handleUndoDelete,
    ]
  );

  const renameStep = useCallback(
    (stepId: string, newName: string) => {
      const trimmed = newName.trim();
      if (trimmed) {
        const currentSteps = stepConfigurations ?? [];
        if (isStepNameDuplicate(trimmed, currentSteps, stepId)) return;
        const targetIndex = resolveStepIndex(stepId, currentSteps);
        const updater = (prev: StepConfiguration[] | null) =>
          (prev ?? []).map((step, i) =>
            i === targetIndex ? { ...step, display_name: trimmed } : step
          );
        setStepConfigurations(updater);
        // Sync rename to the other atom if available
        if (syncRename) {
          syncRename(updater);
        }
      }
      setRenamingStepId(null);
    },
    [stepConfigurations, setStepConfigurations, syncRename]
  );

  const handleStepBarDragStart = useCallback((index: number) => {
    setDraggedIndex(index);
  }, []);

  const handleStepBarDragOver = useCallback(
    (index: number, position: DropPosition) => {
      if (draggedIndex !== null && draggedIndex !== index) {
        setDropTarget({ index, position });
      }
    },
    [draggedIndex]
  );

  const handleStepBarDragEnd = useCallback(() => {
    setDraggedIndex(null);
    setDropTarget(null);
  }, []);

  /** Moves one step and keeps the active step selected. Returns the new list, or null when nothing moved. */
  const applyMove = useCallback(
    (fromIndex: number, toIndex: number): StepConfiguration[] | null => {
      if (stepConfigurations === null) return null;
      if (fromIndex < 0 || fromIndex >= stepConfigurations.length) return null;
      const insertIndex = Math.min(Math.max(toIndex, 0), stepConfigurations.length - 1);
      if (fromIndex === insertIndex) return null;

      const newSteps = [...stepConfigurations];
      const [movedStep] = newSteps.splice(fromIndex, 1);
      newSteps.splice(insertIndex, 0, movedStep);
      setStepConfigurations(newSteps);

      // Follow the previously active step to its new position. It is the same step, so it keeps its sub-step.
      const currentActiveStep = stepConfigurations[currentStepIndex];
      if (currentActiveStep) {
        const newActiveIndex = newSteps.indexOf(currentActiveStep);
        if (newActiveIndex !== -1 && newActiveIndex !== currentStepIndex) {
          setCurrentStepIndex(newActiveIndex);
        }
      }

      return newSteps;
    },
    [stepConfigurations, currentStepIndex, setStepConfigurations, setCurrentStepIndex]
  );

  const handleStepBarDrop = useCallback(
    (targetIndex: number): ReadonlyArray<Nullable<string>> | null => {
      if (draggedIndex === null || dropTarget === null) {
        handleStepBarDragEnd();
        return null;
      }

      let insertIndex = targetIndex;
      if (dropTarget.position === DropPosition.After) {
        insertIndex = targetIndex + 1;
      }

      // Adjust if dragging from above/left to below/right
      if (draggedIndex < insertIndex) {
        insertIndex -= 1;
      }

      const newSteps = applyMove(draggedIndex, insertIndex);
      handleStepBarDragEnd();

      return newSteps?.map((step) => step.smid) ?? null;
    },
    [draggedIndex, dropTarget, applyMove, handleStepBarDragEnd]
  );

  const moveStep = useCallback(
    (fromIndex: number, toIndex: number) => {
      applyMove(fromIndex, toIndex);
    },
    [applyMove]
  );

  const handleStartRename = useCallback((stepId: string) => {
    setRenamingStepId(stepId);
  }, []);

  const handleRenameConfirm = useCallback(
    (stepId: string, newName: string) => {
      renameStep(stepId, newName);
    },
    [renameStep]
  );

  const handleRenameCancel = useCallback(() => {
    setRenamingStepId(null);
  }, []);

  const effectiveStepCount = stepConfigurations?.length ?? 0;

  const canGoBack = currentStepIndex > 0;
  const canGoForward = currentStepIndex < effectiveStepCount - 1;

  const navigateToStep = useCallback(
    (target: number | 'first' | 'last' | 'next' | 'prev') => {
      let targetIndex: number;
      switch (target) {
        case 'first':
          targetIndex = 0;
          break;
        case 'last':
          targetIndex = Math.max(0, effectiveStepCount - 1);
          break;
        case 'next':
          targetIndex = currentStepIndex + 1 >= effectiveStepCount ? 0 : currentStepIndex + 1;
          break;
        case 'prev':
          targetIndex = Math.max(0, currentStepIndex - 1);
          break;
        default:
          targetIndex = target;
      }
      const interceptor = getStepSwitchInterceptor();
      if (interceptor) {
        // The form's step switch moves the index itself, so only the sub-step is left to reset here.
        interceptor(targetIndex);
        setSubStepIndex(0);
        return;
      }
      moveToStepIndex(targetIndex);
    },
    [currentStepIndex, effectiveStepCount, moveToStepIndex, setSubStepIndex]
  );

  const goToPreviousStep = useCallback(() => navigateToStep('prev'), [navigateToStep]);
  const goToNextStep = useCallback(() => navigateToStep('next'), [navigateToStep]);
  const goToFirstStep = useCallback(() => navigateToStep('first'), [navigateToStep]);
  const goToLastStep = useCallback(() => navigateToStep('last'), [navigateToStep]);

  /** Dismiss all active undo toasts (add/delete) without triggering undo. */
  const dismissPendingUndoToasts = useCallback(() => {
    for (const key of activeUndoToastKeys) {
      closeToast(key);
    }
    activeUndoToastKeys.clear();
    undoDeleteSnapshotsRef.current.clear();
    undoedDeleteToastKeysRef.current.clear();
    undoAddSnapshotRef.current = null;
  }, [closeToast]);

  const captureSteps = useCallback((): StepsSnapshot => {
    const steps = (flushInterceptor ? flushInterceptor() : null) ?? stepConfigurations ?? [];
    return {
      steps: [...steps],
      // Clamped for the same reason `insertStep` clamps its source index: the shared index can be past the end of the
      // form's list.
      stepIndex: steps.length > 0 ? Math.min(Math.max(currentStepIndex, 0), steps.length - 1) : 0,
    };
  }, [flushInterceptor, stepConfigurations, currentStepIndex]);

  const restoreSteps = useCallback(
    ({ steps, stepIndex }: StepsSnapshot) => {
      // An undo toast left open would restore a list from before the snapshot was put back.
      dismissPendingUndoToasts();
      setStepConfigurations(steps);
      moveToStepIndex(stepIndex);
      const restoredStep = steps[stepIndex];
      if (restoredStep) onActiveStepReplaced?.(restoredStep);
    },
    [dismissPendingUndoToasts, setStepConfigurations, moveToStepIndex, onActiveStepReplaced]
  );

  return {
    steps: stepConfigurations,
    currentStepIndex,
    initializeSteps,
    resetSteps,
    insertStep,
    addStep,
    deleteStep,
    renameStep,
    setCurrentStepIndex,
    moveToStepIndex,
    stepTotal,
    canGoBack,
    canGoForward,
    goToNextStep,
    goToPreviousStep,
    goToFirstStep,
    goToLastStep,
    draggedIndex,
    dropTarget,
    handleStepBarDragStart,
    handleStepBarDragOver,
    handleStepBarDragEnd,
    handleStepBarDrop,
    moveStep,
    captureSteps,
    restoreSteps,
    renamingStepId,
    handleStartRename,
    handleRenameConfirm,
    handleRenameCancel,
    dismissPendingUndoToasts,
  };
};

/**
 * Step operations for the viewer (clinical read mode).
 * Reads/writes the versioned steppedHPConfigurations atom.
 * Rename sync to the form atom is gated on formSteps != null.
 */
export const useViewerStepOperations = (): UseHPStepOperationsReturn => {
  const viewerVersioned = useRecoilValue(steppedHPConfigurations);
  const viewerSteps = getStepsFromVersioned(viewerVersioned);
  // Step CRUD is a user intent: the steps move, the baseline does not, so the edit reads as unsaved.
  const { stageViewerSteps } = useClinicalStepWriters();

  const formSteps = useRecoilValue(hpFormStepConfigurations);
  const setFormSteps = useSetRecoilState(hpFormStepConfigurations);

  const setSteps = useCallback(
    (updater: StepsUpdater) => {
      stageViewerSteps(updater);
    },
    [stageViewerSteps]
  );

  // Only sync renames to the form atom when the form has active data (E1 fix).
  // When formSteps is null (form closed), skip the write to avoid corrupting
  // the form atom from null → [].
  const setOtherSteps = useCallback(
    (updater: StepsUpdater) => {
      setFormSteps((prev) => {
        if (prev == null) return prev;
        return typeof updater === 'function' ? updater(prev) : updater;
      });
    },
    [setFormSteps]
  );

  return useStepOperationsCore({
    steps: viewerSteps,
    setSteps,
    syncRename: formSteps != null ? setOtherSteps : null,
    flushInterceptor: null,
    // Clinical add-step reaches the layout atoms through `useClinicalStepOperations`, which applies
    // the new step's layouts itself.
    onStepAdded: null,
    onActiveStepReplaced: null,
    ownsStepIndex: true,
  });
};

/**
 * Step operations for the HP edit form.
 * Reads/writes the plain hpFormStepConfigurations atom.
 * Flushes form edits before add/delete via getFormFlushInterceptor.
 */
export const useFormStepOperations = (): UseHPStepOperationsReturn => {
  const [formSteps, setFormSteps] = useRecoilState(hpFormStepConfigurations);

  const setSteps = useCallback(
    (updater: StepsUpdater) => {
      setFormSteps(updater);
    },
    [setFormSteps]
  );

  // The form writer, for the same reason `useStepFormSync` uses it on a step switch: while the form is
  // open the case override is the layer that governs, and `applyProtocolLayouts` would clear it.
  const { applyFormStepLayouts } = useViewerLayoutWriters();
  const onStepAdded = useCallback(
    (step: StepConfiguration) => {
      applyFormStepLayouts(stepLayoutsToLayouts(step.layouts));
    },
    [applyFormStepLayouts]
  );

  return useStepOperationsCore({
    steps: formSteps,
    setSteps,
    // No rename sync. This used to mirror a form rename into the viewer's steps via
    // `stageViewerSteps` "so the step bar agrees", but a *staged* write is exactly what the form must
    // not make: it changed the step's name in the clinical view immediately, marked the step dirty
    // against its baseline, and put the change somewhere Cancel cannot reach -- `closeAndReset`
    // restores layouts and the step index, not the viewer's step names. So renaming a step and then
    // cancelling, or confirming Discard, left the rename applied and the step dirty (VX-4814).
    //
    // The data flow is one-way by design: staged clinical edits feed the form, and the form reaches
    // clinical state only through a successful submit. The rename lives in
    // `hpFormStepConfigurations` until then, which is what the payload is built from
    // (`flushCurrentStepToConfigurations`), so submitting still persists it. The cost is that the
    // clinical step bar keeps the saved name while the form is open, which is what it should show:
    // the name on the server, not one the user may still discard.
    syncRename: null,
    flushInterceptor: getFormFlushInterceptor(),
    onStepAdded,
    // Nothing yet: loading the arriving step into the form ships with the Step Library (#10031), the first caller
    // that replaces the active step with one the form is not already showing.
    onActiveStepReplaced: null,
    ownsStepIndex: false,
  });
};

/**
 * @deprecated Use useViewerStepOperations() or useFormStepOperations() directly.
 * Kept for backward compatibility during migration.
 */
export const useHPStepOperations = (options?: {
  formMode?: boolean;
}): UseHPStepOperationsReturn => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return options?.formMode ? useFormStepOperations() : useViewerStepOperations();
};
